import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Inbox, Clock, CheckCircle2, Phone, Mail, MapPin, Loader2, RefreshCw } from "lucide-react";
import { Logo } from "../components/Logo";
import { api } from "../lib/api";

const STATUSES = ["new", "contacted", "completed"];
const STATUS_STYLE = {
  new: "bg-gold/15 text-gold border-gold/30",
  contacted: "bg-blue-50 text-blue-700 border-blue-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function AdminDashboard() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/quotes");
      setQuotes(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("wcs_token");
        nav("/admin/login");
      } else {
        toast.error("Failed to load quotes");
      }
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => {
    if (!localStorage.getItem("wcs_token")) { nav("/admin/login"); return; }
    load();
  }, [load, nav]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/quotes/${id}`, { status });
      setQuotes((qs) => qs.map((q) => (q.id === id ? data : q)));
      toast.success(`Marked ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const logout = () => { localStorage.removeItem("wcs_token"); nav("/admin/login"); };

  const filtered = filter === "all" ? quotes : quotes.filter((q) => q.status === filter);
  const stats = {
    total: quotes.length,
    new: quotes.filter((q) => q.status === "new").length,
    completed: quotes.filter((q) => q.status === "completed").length,
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      <div className="flex">
        {/* Sidebar */}
        <aside className="fixed hidden h-screen w-64 flex-col justify-between border-r border-navy/10 bg-navy p-6 lg:flex">
          <div>
            <Logo dark />
            <nav className="mt-10 space-y-1">
              <div className="flex items-center gap-3 rounded-xl bg-white/10 px-4 py-3 text-white">
                <Inbox className="h-5 w-5" /> <span className="font-medium">Quote Requests</span>
              </div>
            </nav>
          </div>
          <button onClick={logout} data-testid="logout-btn" className="flex items-center gap-3 rounded-xl px-4 py-3 text-white/70 transition-colors hover:bg-white/10 hover:text-white">
            <LogOut className="h-5 w-5" /> Log out
          </button>
        </aside>

        {/* Main */}
        <main className="flex-1 p-5 lg:ml-64 lg:p-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-display text-3xl font-extrabold text-navy">Quote Requests</h1>
              <p className="mt-1 text-sm text-navy/50">Manage incoming courier quote submissions</p>
            </div>
            <div className="flex gap-2">
              <button onClick={load} data-testid="refresh-btn" className="flex items-center gap-2 rounded-full border border-navy/15 bg-white px-4 py-2 text-sm font-semibold text-navy hover:bg-navy/5">
                <RefreshCw className="h-4 w-4" /> Refresh
              </button>
              <button onClick={logout} className="rounded-full border border-navy/15 bg-white px-4 py-2 text-sm font-semibold text-navy lg:hidden">Log out</button>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-8 grid grid-cols-3 gap-4">
            {[
              { label: "Total", value: stats.total, icon: Inbox },
              { label: "New", value: stats.new, icon: Clock },
              { label: "Completed", value: stats.completed, icon: CheckCircle2 },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.label} data-testid={`stat-${s.label.toLowerCase()}`} className="rounded-2xl border border-navy/10 bg-white p-5">
                  <Icon className="h-5 w-5 text-gold" />
                  <p className="mt-3 font-display text-3xl font-extrabold text-navy">{s.value}</p>
                  <p className="text-xs font-medium uppercase tracking-wider text-navy/50">{s.label}</p>
                </div>
              );
            })}
          </div>

          {/* Filter */}
          <div className="mt-8 flex flex-wrap gap-2">
            {["all", ...STATUSES].map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
                className={`rounded-full px-4 py-1.5 text-sm font-semibold capitalize transition-colors ${filter === f ? "bg-navy text-white" : "border border-navy/15 bg-white text-navy hover:bg-navy/5"}`}>
                {f}
              </button>
            ))}
          </div>

          {/* List */}
          <div className="mt-6 space-y-4">
            {loading ? (
              <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-navy/40" /></div>
            ) : filtered.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-navy/15 bg-white py-20 text-center text-navy/40" data-testid="empty-state">No quote requests yet.</div>
            ) : (
              filtered.map((q) => (
                <div key={q.id} data-testid={`quote-row-${q.id}`} className="rounded-2xl border border-navy/10 bg-white p-6">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="font-display text-xl font-bold text-navy">{q.name}</h3>
                        <span className={`rounded-full border px-2.5 py-0.5 text-xs font-bold capitalize ${STATUS_STYLE[q.status]}`}>{q.status}</span>
                      </div>
                      {q.business_name && <p className="text-sm text-navy/50">{q.business_name}</p>}
                      {q.service_needed && <p className="mt-1 text-xs font-bold uppercase tracking-wider text-gold">{q.service_needed}</p>}
                    </div>
                    <div className="flex gap-2">
                      {STATUSES.map((s) => (
                        <button key={s} onClick={() => updateStatus(q.id, s)} data-testid={`set-status-${s}-${q.id}`}
                          disabled={q.status === s}
                          className={`rounded-full px-3 py-1 text-xs font-semibold capitalize transition-colors ${q.status === s ? "bg-navy text-white" : "border border-navy/15 text-navy hover:bg-navy/5"}`}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="mt-4 grid gap-2 border-t border-navy/10 pt-4 text-sm text-navy/70 sm:grid-cols-2">
                    <a href={`tel:${q.phone}`} className="flex items-center gap-2 hover:text-navy"><Phone className="h-4 w-4 text-gold" /> {q.phone}</a>
                    <a href={`mailto:${q.email}`} className="flex items-center gap-2 hover:text-navy"><Mail className="h-4 w-4 text-gold" /> {q.email}</a>
                    {q.pickup_location && <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-gold" /> Pickup: {q.pickup_location}</span>}
                    {q.delivery_location && <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-gold" /> Drop: {q.delivery_location}</span>}
                  </div>
                  {q.message && <p className="mt-3 rounded-xl bg-navy/5 p-3 text-sm text-navy/70">{q.message}</p>}
                  <p className="mt-3 text-xs text-navy/40">{new Date(q.created_at).toLocaleString()}</p>
                </div>
              ))
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
